import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import moment from 'moment';
import { DateFormatInput } from 'material-ui-next-pickers';
import {
    Typography,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Card,
    CardContent,
    TablePagination,
} from '@material-ui/core/';

import Loading from 'Console/components/common/loading/Loading';

import { fetchAudit } from 'Console/data/audit/actions';
import { pushNotificationToStack } from 'Console/data/notification/actions';
import { selectAudit, selectAuditHasError, selectAuditIsLoading } from 'Console/data/audit/selectors';
import { MOMENT_DATETIME_FORMAT_DISPLAY_SECONDS } from 'Console/constants/date';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: 24,
    },
    textField: {
        width: '20%',
        paddingRight: 30,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            paddingBottom: 30,
        },
    },
    cssUnderline: {
        '&:before': {
            borderBottom: `3px solid rgba(155, 174, 0, 1)`,
        },
        '&:hover:not([disabled]):not([focused]):not([error]):before': {
            borderBottom: `3px solid rgb(108, 121, 0) !important`,
        },
    },
    cssRoot: {
        position: 'initial',
        lineHeight: theme.typography.pxToRem(8),
        height: 30,
    },
});

class Audit extends Component {
    state = {
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Today - 30 days as default
        dateTo: new Date(),
        pageNumber: 0,
        rowsPerPage: 20,
    };

    componentDidMount() {
        const { fetchAudit } = this.props;
        const { dateFrom, dateTo } = this.state;
        fetchAudit({ dateFrom, dateTo });
    }

    onChangeDateFrom = (date) => this.onChangeState('dateFrom', date);

    onChangeDateTo = (date) => this.onChangeState('dateTo', date);

    onChangeState = (key, value) => {
        this.setState({
            [key]: value,
        });
    };

    filterResults = (params) => () => {
        const { fetchAudit } = this.props;
        const { dateFrom, dateTo } = this.state;
        fetchAudit({ ...params, dateFrom, dateTo });
    };

    render() {
        const { classes, intl, isLoading, audit } = this.props;
        const { pageNumber, rowsPerPage, dateTo, dateFrom } = this.state;

        if (isLoading) {
            return <Loading minHeight="300" />;
        }

        return (
            <Grid container className={classes.root}>
                <Grid container spacing={24} justify="center" alignItems="flex-end">
                    <Grid item xs={12} md={10}>
                        <Grid
                            style={{ marginBottom: '28px' }}
                            container
                            justify="space-between"
                            alignItems="flex-end"
                        >
                            <Grid item>
                                <Typography variant="h6">
                                    {intl.formatMessage({ id: 'Audit.title' })}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container justify="flex-start" alignItems="flex-end">
                            <DateFormatInput
                                name="date-start-input"
                                label={intl.formatMessage({ id: 'Date.from' })}
                                value={dateFrom}
                                onChange={this.onChangeDateFrom}
                                className={classes.textField}
                                variant="standard"
                                InputProps={{
                                    classes: {
                                        underline: classes.cssUnderline,
                                        root: classes.cssRoot,
                                        input: classes.dateInputInput,
                                    },
                                }}
                                max={new Date()}
                            />

                            <DateFormatInput
                                name="date-end-input"
                                label={intl.formatMessage({ id: 'Date.to' })}
                                value={dateTo}
                                onChange={this.onChangeDateTo}
                                className={classes.textField}
                                InputProps={{
                                    classes: {
                                        underline: classes.cssUnderline,
                                        root: classes.cssRoot,
                                    },
                                }}
                                max={new Date()}
                            />
                            <Grid item>
                                <Typography>
                                    <Button
                                        id="yeself-filter-button"
                                        variant="contained"
                                        color="primary"
                                        onClick={this.filterResults()}
                                    >
                                        {intl.formatMessage({ id: 'Buttons.filter' })}
                                    </Button>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableTitles}>
                                                {intl.formatMessage({ id: 'Audit.table.time' })}
                                            </TableCell>
                                            <TableCell className={classes.tableTitles} align="right">
                                                {intl.formatMessage({ id: 'Audit.table.event' })}
                                            </TableCell>
                                            <TableCell className={classes.tableTitles} align="right">
                                                {intl.formatMessage({ id: 'Audit.table.userName' })}
                                            </TableCell>
                                            <TableCell className={classes.tableTitles} align="right">
                                                {intl.formatMessage({ id: 'Audit.table.ipAddress' })}
                                            </TableCell>
                                            <TableCell className={classes.tableTitles} align="right">
                                                {intl.formatMessage({ id: 'Audit.table.result' })}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {audit
                                            .slice(
                                                pageNumber * rowsPerPage,
                                                (pageNumber + 1) * rowsPerPage
                                            )
                                            .map((row) => (
                                                <TableRow
                                                    key={row.clientId}
                                                    className={classes.tableRow}
                                                >
                                                    <TableCell
                                                        className={classes.tableCell}
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        {moment(row.time, 'x').format(
                                                            MOMENT_DATETIME_FORMAT_DISPLAY_SECONDS
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        className={classes.tableCell}
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        {row.type}
                                                    </TableCell>
                                                    {row.details && row.details.username ? (
                                                        <TableCell
                                                            className={classes.tableCell}
                                                            align="right"
                                                        >
                                                            {row.details.username}
                                                        </TableCell>
                                                    ) : (
                                                        <TableCell
                                                            className={classes.tableCell}
                                                            align="right"
                                                        >
                                                            -
                                                        </TableCell>
                                                    )}
                                                    <TableCell
                                                        className={classes.tableCell}
                                                        align="right"
                                                    >
                                                        {row.ipAddress}
                                                    </TableCell>
                                                    <TableCell
                                                        className={classes.tableCell}
                                                        align="right"
                                                    >
                                                        {row.error}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    component="div"
                                    count={audit.length}
                                    rowsPerPage={rowsPerPage}
                                    page={pageNumber}
                                    backIconButtonProps={{
                                        'aria-label': 'Previous Page',
                                    }}
                                    nextIconButtonProps={{
                                        'aria-label': 'Next Page',
                                    }}
                                    onChangePage={(event, pageNumber) =>
                                        this.onChangeState('pageNumber', pageNumber)
                                    }
                                    onChangeRowsPerPage={(e) =>
                                        this.onChangeState('rowsPerPage', e.target.value)
                                    }
                                    labelRowsPerPage={intl.formatMessage({ id: 'Table.rowsPerPage' })}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `${from}-${to} ${intl.formatMessage({
                                            id: 'Table.of',
                                        })} ${count}`
                                    }
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

export default connect(
    (state) => ({
        audit: selectAudit(state),
        isLoading: selectAuditIsLoading(state),
        hasError: selectAuditHasError(state),
    }),
    {
        fetchAudit,
        pushNotificationToStack,
    }
)(withStyles(styles)(injectIntl(Audit)));
